"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Step = {
  number: string;
  title: string;
  description: string;
  pullquote: string;
};
const steps: Step[] = [{
  number: "01",
  title: "Initial Consultation",
  description: "We start with a detailed walkthrough of your space. We listen to your vision, assess the surfaces, and discuss the specific finishes that will bring your home to life.",
  pullquote: "Precision begins with listening."
}, {
  number: "02",
  title: "Surface Preparation",
  description: "A perfect finish requires a perfect foundation. We meticulously clean, sand, and repair every surface to ensure the paint adheres flawlessly and lasts for years.",
  pullquote: "The secret is in the prep."
}, {
  number: "03",
  title: "Color & Material Selection",
  description: "We guide you through our curated palette of premium paints. Whether you seek a bold statement or a serene retreat, we ensure the perfect match for your lighting and style.",
  pullquote: "Colors that define your space."
}, {
  number: "04",
  title: "Professional Application",
  description: "Our master painters apply each coat with steady, expert hands. We use professional-grade tools and techniques to ensure smooth, even coverage with crisp, clean lines.",
  pullquote: "Craftsmanship in every stroke."
}, {
  number: "05",
  title: "Final Inspection & Reveal",
  description: "We walk through the finished project with you to ensure every detail meets our high standards. Your satisfaction is the final brushstroke of our process.",
  pullquote: "Excellence is our standard."
}];
function TimelineStep({
  step,
  index
}: {
  step: Step;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const isLeft = index % 2 === 0;
  return <div ref={ref} data-index={index} className={`relative md:grid md:grid-cols-2 md:gap-12 lg:gap-20 transition-all duration-[900ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
    transitionDelay: `${index * 80}ms`
  }}>
      {}
      <div className="absolute left-4 md:left-1/2 top-8 md:top-10 -translate-x-1/2 z-10" aria-hidden="true">
        <div className="w-3 h-3 rotate-45 bg-primary shadow-md" />
      </div>

      {}
      <div className={`hidden md:block ${isLeft ? "md:pr-16 lg:pr-20 md:text-right" : ""}`}>
        {isLeft && <StepContent step={step} align="right" />}
      </div>

      {}
      <div className={`hidden md:block ${!isLeft ? "md:pl-16 lg:pl-20 md:text-left" : ""}`}>
        {!isLeft && <StepContent step={step} align="left" />}
      </div>

      {}
      <div className="md:hidden pl-12">
        <StepContent step={step} align="left" />
      </div>
    </div>;
}
function StepContent({
  step,
  align
}: {
  step: Step;
  align: "left" | "right";
}) {
  return <div className="relative py-6">
      {}
      <div aria-hidden="true" className={`pointer-events-none select-none absolute -top-4 ${align === "right" ? "right-0" : "left-0"} font-serif italic text-[5rem] leading-none text-primary/20`}>
        {step.number}
      </div>

      <div className="relative">
        <h3 className="font-default text-[1.75rem] leading-tight text-foreground font-medium" style={{
        fontFamily: "var(--font-serif)"
      }}>
          {step.title}
        </h3>
        <p className="mt-3 text-[17px] leading-relaxed text-muted-foreground" style={{
        fontFamily: "var(--font-sans)"
      }}>
          {step.description}
        </p>
        <p className="mt-4 font-default italic text-primary text-base" style={{
        fontFamily: "var(--font-serif)"
      }}>
          &ldquo;{step.pullquote}&rdquo;
        </p>
      </div>
    </div>;
}
export default function YourPathToANewLook() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="925" id="our-painting-process" className="bg-background py-40 px-6 md:px-10">
      {}
      <div ref={headerRef} className={`max-w-3xl mx-auto text-center transition-all duration-[900ms] ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <p className="text-sm tracking-[0.2em] uppercase text-primary font-default" style={{
        fontFamily: "var(--font-mono)"
      }}>
          &middot; Our Process &middot;
        </p>
        <h2 className="mt-6 font-default text-4xl md:text-5xl lg:text-6xl leading-[1.1] text-foreground" style={{
        fontFamily: "var(--font-serif)"
      }}>
          From the first brushstroke to the final reveal.
        </h2>
        <p className="mt-6 text-lg text-muted-foreground leading-relaxed font-default">
          We bring professional precision to every project, ensuring your home receives the care and quality it deserves.
        </p>
      </div>

      {}
      <div className="relative max-w-4xl mx-auto mt-24">
        {}
        <div aria-hidden="true" className="absolute top-0 bottom-0 left-4 md:left-1/2 -translate-x-1/2 w-px bg-border" />

        <div className="space-y-20 md:space-y-24">
          {steps.map((step, i) => <TimelineStep key={step.number} step={step} index={i} />)}
        </div>
      </div>
    </section>;
}